import { useEffect, useState, type FormEvent } from 'react';
import type { AuthenticationOutcome } from '@ayumia/contracts';
import { ApiError } from '../api/client';
import type { AuthClient } from '../api/auth-client';
import { Loading } from '../states/States';
import { FormError } from './SignInPage';
import { authFailure } from './auth-errors';
import { FormMessage } from '../design-system/FormMessage';
import { SubmitButton } from '../design-system/fields';
import { PasswordInput } from '../design-system/fields';

/**
 * Sécurité du compte (ADR-091 §4-§5) : activer le second facteur (TOTP), élever la session avant
 * une opération sensible, changer son mot de passe. Chaque geste repasse au serveur : la page ne
 * décide rien, elle présente l'état de `GET /auth/me`.
 */

const LEVELS: Record<string, string> = { single_factor: 'un facteur', multi_factor: 'deux facteurs', strong: 'forte' };

const failure = (error: unknown): string => authFailure(error).text;

export function AccountSecurityPage({ client }: { client: AuthClient }) {
  const [me, setMe] = useState<AuthenticationOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorForm, setErrorForm] = useState<'general' | 'password'>('general');
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [enrollment, setEnrollment] = useState<{ secret: string; uri: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [stepUpCode, setStepUpCode] = useState('');
  const [passwords, setPasswords] = useState({ current: '', next: '', confirmation: '' });

  const reload = (): void => {
    client.me().then(setMe, (caught: unknown) => setError(failure(caught)));
  };
  useEffect(reload, [client]);

  const run = (form: 'general' | 'password', work: () => Promise<void>) => (event: FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setErrorForm(form);
    setNotice(null);
    work()
      .catch((caught: unknown) => setError(failure(caught)))
      .finally(() => setBusy(false));
  };

  // Une erreur s'affiche près du formulaire qui l'a produite : le mot de passe a son propre emplacement.
  const errorFor = (form: 'general' | 'password'): string | null => (errorForm === form ? error : null);

  if (me === null) return error === null ? <Loading /> : <FormError message={error} />;
  const mfaEnabled = me.identity?.mfa_enabled === true;

  return (
    <section className="ay-stack ay-stack--column ay-account" aria-labelledby="ay-account-title">
      <h1 id="ay-account-title">Sécurité du compte</h1>
      <p className="ay-text--muted">
        {me.identity?.display_name} · {me.identity?.login_email ?? 'sans adresse de connexion'} · session à {LEVELS[me.session?.authentication_level ?? ''] ?? '—'}
      </p>
      <FormError message={errorFor('general')} />
      {notice !== null && (
        <p className="ay-banner" role="status">
          {notice}
        </p>
      )}

      <section className="ay-card ay-stack ay-stack--column" aria-labelledby="ay-account-mfa">
        <h2 id="ay-account-mfa">Vérification en deux étapes</h2>
        {recoveryCodes !== null ? (
          <>
            <p>Codes de récupération, à conserver hors de l’ordinateur ; ils ne seront plus affichés :</p>
            <ul className="ay-signin__codes" aria-label="Codes de récupération">
              {recoveryCodes.map((recovery) => (
                <li key={recovery}>
                  <code>{recovery}</code>
                </li>
              ))}
            </ul>
          </>
        ) : mfaEnabled ? (
          <p>Activée : une application d’authentification protège ce compte.</p>
        ) : enrollment === null ? (
          <form className="ay-stack ay-stack--column" onSubmit={run('general', async () => {
            const outcome = await client.startEnrollment({ current_password: currentPassword });
            setCurrentPassword('');
            if (outcome.totp !== undefined) setEnrollment({ secret: outcome.totp.secret, uri: outcome.totp.otpauth_uri });
          })}>
            <p className="ay-text--muted">Les opérations d’administration exigent un second facteur. Confirmez votre mot de passe pour obtenir la clé à saisir dans une application d’authentification.</p>
            <label className="ay-field">
              <span className="ay-field__label">Mot de passe actuel</span>
              <PasswordInput autoComplete="current-password" required value={currentPassword} onValueChange={setCurrentPassword} />
            </label>
            <button type="submit" className="ay-button" disabled={busy}>
              Activer
            </button>
          </form>
        ) : (
          <form className="ay-stack ay-stack--column" onSubmit={run('general', async () => {
            const outcome = await client.confirmEnrollment(code.trim());
            setCode('');
            setEnrollment(null);
            setRecoveryCodes(outcome.recovery_codes ?? []);
            reload();
          })}>
            <p>
              Clé : <code className="ay-signin__secret">{enrollment.secret.replace(/(.{4})/g, '$1 ').trim()}</code> · <a href={enrollment.uri}>ajouter le compte</a>
            </p>
            <label className="ay-field">
              <span className="ay-field__label">Code affiché</span>
              <input inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(event) => setCode(event.target.value)} />
            </label>
            <button type="submit" className="ay-button" disabled={busy}>
              Confirmer
            </button>
          </form>
        )}
      </section>

      {mfaEnabled && (
        <section className="ay-card ay-stack ay-stack--column" aria-labelledby="ay-account-step-up">
          <h2 id="ay-account-step-up">Élever la session</h2>
          <form className="ay-stack ay-stack--column" onSubmit={run('general', async () => {
            await client.stepUp(stepUpCode.trim());
            setStepUpCode('');
            setNotice('Session élevée : les opérations sensibles sont permises pendant quelques minutes.');
            reload();
          })}>
            <p className="ay-text--muted">Avant une opération sensible (configuration, secrets, révocation), saisissez un code de l’application ou un code de récupération.</p>
            <label className="ay-field">
              <span className="ay-field__label">Code d’élévation</span>
              <input inputMode="numeric" autoComplete="one-time-code" required value={stepUpCode} onChange={(event) => setStepUpCode(event.target.value)} />
            </label>
            <button type="submit" className="ay-button" disabled={busy}>
              Élever
            </button>
          </form>
        </section>
      )}

      <section className="ay-card ay-stack ay-stack--column" aria-labelledby="ay-account-password">
        <h2 id="ay-account-password">Mot de passe</h2>
        <form className="ay-form" onSubmit={run('password', async () => {
          if (passwords.next !== passwords.confirmation) throw new ApiError('VALIDATION_ERROR', 'PASSWORD_MISMATCH', 'Les deux saisies diffèrent.', 400);
          await client.changePassword(passwords.current, passwords.next);
          setPasswords({ current: '', next: '', confirmation: '' });
          setNotice('Mot de passe changé. Vos autres sessions ont été fermées.');
        })}>
          <label className="ay-field">
            <span className="ay-field__label">Mot de passe actuel</span>
            <PasswordInput autoComplete="current-password" required value={passwords.current} onValueChange={(value) => setPasswords({ ...passwords, current: value })} />
          </label>
          <label className="ay-field">
            <span className="ay-field__label">Nouveau mot de passe</span>
            <PasswordInput autoComplete="new-password" minLength={12} required value={passwords.next} onValueChange={(value) => setPasswords({ ...passwords, next: value })} />
          </label>
          <label className="ay-field">
            <span className="ay-field__label">Confirmation</span>
            <PasswordInput autoComplete="new-password" required value={passwords.confirmation} onValueChange={(value) => setPasswords({ ...passwords, confirmation: value })} />
          </label>
          <div className="ay-form__submit">
            <SubmitButton busy={busy} busyLabel="Enregistrement…" variant="inline">
              Changer le mot de passe
            </SubmitButton>
          </div>
          <FormMessage message={errorFor('password')} />
        </form>
      </section>
    </section>
  );
}
